import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, FileText, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Clock, Send,
  X, RefreshCw, Download, FileSpreadsheet,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';

// ==================== Types ====================
interface GSTR1Invoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerName: string;
  customerGstin: string | null;
  category: 'B2B' | 'B2CS' | 'CDNR' | 'CDNUR';
  taxableValue: number | string;
  cgst: number | string;
  sgst: number | string;
  igst: number | string;
  totalTax: number | string;
  status: 'DRAFT' | 'PREPARED' | 'SUBMITTED' | 'FILED' | 'ERROR';
  errorMessage: string | null;
}

interface GSTR1Stats {
  b2bCount: number;
  b2csCount: number;
  cdnrCount: number;
  cdnurCount: number;
  totalTaxableValue: number | string;
  totalTax: number | string;
  status: 'DRAFT' | 'PREPARED' | 'SUBMITTED' | 'FILED' | 'ERROR';
}

interface GSTR3BSummary {
  outwardSupplies: {
    taxable: number | string;
    exempt: number | string;
    nilRated: number | string;
  };
  itcAvailable: {
    cgst: number | string;
    sgst: number | string;
    igst: number | string;
  };
  netTaxPayable: {
    cgst: number | string;
    sgst: number | string;
    igst: number | string;
    total: number | string;
  };
  status: 'DRAFT' | 'PREPARED' | 'SUBMITTED' | 'FILED' | 'ERROR';
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PREPARED: 'bg-blue-100 text-blue-700',
  SUBMITTED: 'bg-yellow-100 text-yellow-700',
  FILED: 'bg-green-100 text-green-700',
  ERROR: 'bg-red-100 text-red-700',
};

const CATEGORY_LABELS: Record<string, string> = {
  B2B: 'B2B Invoices',
  B2CS: 'B2C (Small) Invoices',
  CDNR: 'Credit/Debit Notes (Registered)',
  CDNUR: 'Credit/Debit Notes (Unregistered)',
};

// ==================== Period Helpers ====================
function getCurrentFinancialPeriods(): { value: string; label: string }[] {
  const now = new Date();
  const periods: { value: string; label: string }[] = [];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Show last 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    periods.push({ value: val, label: `${months[d.getMonth()]} ${d.getFullYear()}` });
  }
  return periods;
}

function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ==================== Main Component ====================
export function GSTFilingPage() {
  const [activeTab, setActiveTab] = useState<'gstr1' | 'gstr3b'>('gstr1');

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">GST Filing</h1>
        <p className="text-sm text-gray-500">Prepare and file GSTR-1 and GSTR-3B returns via WhiteBooks GSP</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('gstr1')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'gstr1'
              ? 'border-[var(--ink-0)] text-[var(--ink-0)]'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span className="flex items-center gap-2"><FileText size={16} />GSTR-1</span>
        </button>
        <button
          onClick={() => setActiveTab('gstr3b')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'gstr3b'
              ? 'border-[var(--ink-0)] text-[var(--ink-0)]'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span className="flex items-center gap-2"><FileSpreadsheet size={16} />GSTR-3B</span>
        </button>
      </div>

      {activeTab === 'gstr1' && <GSTR1Tab />}
      {activeTab === 'gstr3b' && <GSTR3BTab />}
    </div>
  );
}

// ==================== GSTR-1 Tab ====================
function GSTR1Tab() {
  const periods = getCurrentFinancialPeriods();
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [stats, setStats] = useState<GSTR1Stats | null>(null);
  const [invoices, setInvoices] = useState<GSTR1Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [sortField, setSortField] = useState('invoiceDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedCategory, setExpandedCategory] = useState<string | null>('B2B');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, invoicesRes] = await Promise.all([
        api.get('/gst-compliance/filing/gstr1/stats', { params: { period } }),
        api.get('/gst-compliance/filing/gstr1/invoices', { params: { period, sortBy: sortField, sortDir } }),
      ]);
      setStats(statsRes.data.data ?? null);
      setInvoices(invoicesRes.data.data?.invoices ?? invoicesRes.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load GSTR-1 data');
    } finally {
      setLoading(false);
    }
  }, [period, sortField, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePrepare = async () => {
    setPreparing(true);
    try {
      await api.post('/gst-compliance/filing/gstr1/prepare', { period });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to prepare GSTR-1');
    } finally {
      setPreparing(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit GSTR-1 to GSTN? This will send invoice data to the GST portal. Continue?')) return;
    setSubmitting(true);
    try {
      await api.post('/gst-compliance/filing/gstr1/submit', { period });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to submit GSTR-1');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const groupedInvoices = invoices.reduce<Record<string, GSTR1Invoice[]>>((acc, inv) => {
    if (!acc[inv.category]) acc[inv.category] = [];
    acc[inv.category].push(inv);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading GSTR-1 data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-red-500">
        <AlertTriangle size={32} className="mb-2" />
        <p className="text-sm">{error}</p>
        <button onClick={fetchData} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Period selector + actions */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Period:</label>
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            {periods.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {(!stats || stats.status === 'DRAFT') && (
            <button
              onClick={handlePrepare}
              disabled={preparing}
              className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {preparing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Prepare GSTR-1
            </button>
          )}
          {stats?.status === 'PREPARED' && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Submit to GSTN
            </button>
          )}
          {stats && (
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[stats.status] || STATUS_COLORS.DRAFT}`}>
              {stats.status}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">B2B Invoices</p>
            <p className="mt-1 text-lg font-bold text-gray-900">{stats.b2bCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">B2C Invoices</p>
            <p className="mt-1 text-lg font-bold text-gray-900">{stats.b2csCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Credit/Debit Notes</p>
            <p className="mt-1 text-lg font-bold text-gray-900">{stats.cdnrCount + stats.cdnurCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Total Invoices</p>
            <p className="mt-1 text-lg font-bold text-gray-900">{stats.b2bCount + stats.b2csCount + stats.cdnrCount + stats.cdnurCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Taxable Value</p>
            <p className="mt-1 text-lg font-bold text-[var(--ink-0)]">{formatINR(stats.totalTaxableValue)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs text-gray-500">Total Tax</p>
            <p className="mt-1 text-lg font-bold text-green-600">{formatINR(stats.totalTax)}</p>
          </div>
        </div>
      )}

      {/* Grouped Invoice List */}
      {invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-20 text-gray-400">
          <FileText size={40} className="mb-2" />
          <p className="text-sm">No invoices found for this period</p>
          <p className="text-xs">Click "Prepare GSTR-1" to collect invoices for this filing period.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(['B2B', 'B2CS', 'CDNR', 'CDNUR'] as const).map(category => {
            const items = groupedInvoices[category] || [];
            if (items.length === 0) return null;
            const isExpanded = expandedCategory === category;
            const categoryTax = items.reduce((sum, inv) => sum + Number(inv.totalTax || 0), 0);
            const categoryTaxable = items.reduce((sum, inv) => sum + Number(inv.taxableValue || 0), 0);

            return (
              <div key={category} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : category)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-gray-900">{CATEGORY_LABELS[category]}</h3>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{items.length}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-500">Taxable: {formatINR(categoryTaxable)}</span>
                    <span className="text-xs text-gray-500">Tax: {formatINR(categoryTax)}</span>
                    {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          <th className="cursor-pointer px-4 py-2.5" onClick={() => handleSort('invoiceNumber')}>
                            <span className="flex items-center gap-1">Invoice # <SortIcon field="invoiceNumber" /></span>
                          </th>
                          <th className="cursor-pointer px-4 py-2.5" onClick={() => handleSort('invoiceDate')}>
                            <span className="flex items-center gap-1">Date <SortIcon field="invoiceDate" /></span>
                          </th>
                          <th className="px-4 py-2.5">Customer</th>
                          <th className="px-4 py-2.5 text-right">Taxable</th>
                          <th className="px-4 py-2.5 text-right">CGST</th>
                          <th className="px-4 py-2.5 text-right">SGST</th>
                          <th className="px-4 py-2.5 text-right">IGST</th>
                          <th className="px-4 py-2.5">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {items.map(inv => (
                          <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2.5 font-medium text-[var(--ink-0)]">{inv.invoiceNumber}</td>
                            <td className="px-4 py-2.5 text-gray-600">{formatDate(inv.invoiceDate)}</td>
                            <td className="px-4 py-2.5 text-gray-600">
                              {inv.customerName}
                              {inv.customerGstin && (
                                <span className="ml-1 text-xs text-gray-400 font-mono">({inv.customerGstin})</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right text-gray-900">{formatINR(inv.taxableValue)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600">{formatINR(inv.cgst)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600">{formatINR(inv.sgst)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-600">{formatINR(inv.igst)}</td>
                            <td className="px-4 py-2.5">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[inv.status] || STATUS_COLORS.DRAFT}`}>
                                {inv.status}
                              </span>
                              {inv.errorMessage && (
                                <span className="ml-1 text-xs text-red-500" title={inv.errorMessage}>
                                  <AlertTriangle size={12} className="inline" />
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== GSTR-3B Tab ====================
function GSTR3BTab() {
  const periods = getCurrentFinancialPeriods();
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [summary, setSummary] = useState<GSTR3BSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [filing, setFiling] = useState(false);

  // OTP flow
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/gst-compliance/filing/gstr3b/summary', { params: { period } });
      setSummary(res.data.data ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load GSTR-3B data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePrepare = async () => {
    setPreparing(true);
    try {
      await api.post('/gst-compliance/filing/gstr3b/prepare', { period });
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to prepare GSTR-3B');
    } finally {
      setPreparing(false);
    }
  };

  const handleFileGSTR3B = () => {
    setShowOtpModal(true);
    setOtp('');
  };

  const handleSubmitOtp = async () => {
    if (!otp.trim()) return;
    setOtpSending(true);
    try {
      await api.post('/gst-compliance/filing/gstr3b/file', { period, otp });
      setShowOtpModal(false);
      setOtp('');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to file GSTR-3B');
    } finally {
      setOtpSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading GSTR-3B data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-red-500">
        <AlertTriangle size={32} className="mb-2" />
        <p className="text-sm">{error}</p>
        <button onClick={fetchData} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      {/* Period selector + actions */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Period:</label>
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            {periods.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {(!summary || summary.status === 'DRAFT') && (
            <button
              onClick={handlePrepare}
              disabled={preparing}
              className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {preparing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Prepare GSTR-3B
            </button>
          )}
          {summary?.status === 'PREPARED' && (
            <button
              onClick={handleFileGSTR3B}
              disabled={filing}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {filing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              File GSTR-3B
            </button>
          )}
          {summary && (
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[summary.status] || STATUS_COLORS.DRAFT}`}>
              {summary.status}
            </span>
          )}
        </div>
      </div>

      {!summary ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-20 text-gray-400">
          <FileSpreadsheet size={40} className="mb-2" />
          <p className="text-sm">No GSTR-3B data for this period</p>
          <p className="text-xs">Click "Prepare GSTR-3B" to auto-populate from GSTR-1 and purchase register.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Outward Supplies */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">3.1 Outward Supplies</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5">Nature of Supplies</th>
                    <th className="px-4 py-2.5 text-right">Taxable Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">(a) Outward taxable supplies (other than zero rated, nil rated and exempted)</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.outwardSupplies.taxable)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">(b) Outward taxable supplies (zero rated)</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-600">{formatINR(0)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">(c) Other outward supplies (nil rated, exempted)</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-600">{formatINR(summary.outwardSupplies.exempt)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">(d) Inward supplies (liable to reverse charge)</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-600">{formatINR(0)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">(e) Non-GST outward supplies</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-600">{formatINR(summary.outwardSupplies.nilRated)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ITC Available */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">4. Eligible ITC</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5">Details</th>
                    <th className="px-4 py-2.5 text-right">CGST</th>
                    <th className="px-4 py-2.5 text-right">SGST</th>
                    <th className="px-4 py-2.5 text-right">IGST</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">ITC Available (whether in full or part)</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.itcAvailable.cgst)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.itcAvailable.sgst)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.itcAvailable.igst)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Net Tax Payable */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">6.1 Net Tax Payable</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5">Tax</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">CGST Payable</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.netTaxPayable.cgst)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">SGST Payable</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.netTaxPayable.sgst)}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">IGST Payable</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(summary.netTaxPayable.igst)}</td>
                  </tr>
                  <tr className="bg-[var(--saffron-50)] hover:bg-[var(--saffron-50)]">
                    <td className="px-4 py-3 font-semibold text-gray-900">Total Tax Payable</td>
                    <td className="px-4 py-3 text-right text-lg font-bold text-[var(--ink-0)]">{formatINR(summary.netTaxPayable.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== OTP Modal ==================== */}
      {showOtpModal && (
        <Modal open onClose={() => setShowOtpModal(false)} className="!max-w-sm p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">OTP Verification</h3>
              <RequestCloseButton className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><X size={18} /></RequestCloseButton>
            </div>
            <p className="mb-4 text-sm text-gray-600">
              An OTP has been sent to the GST portal registered mobile number. Enter it below to file GSTR-3B.
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700">OTP *</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-center tracking-widest focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="_ _ _ _ _ _"
              maxLength={6}
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-3">
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleSubmitOtp}
                disabled={otp.length < 6 || otpSending}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {otpSending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                File GSTR-3B
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

export default GSTFilingPage;

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}

